<template>
  <last-data-preview
    v-if="isDataPreviewModalOpened"
    :data="dataForModal.data"
    :is-last-data-modal-opened="isDataPreviewModalOpened"
    :name="selectedMetadata[0]?.name"
    :preview="true"
    @toggle-modal="isDataPreviewModalOpened = false"
  />
  <create-form
    v-if="isCreateModalOpened"
    :is-create-modal-opened="isCreateModalOpened"
    :refresh-data="getAllAssignmentMetadata"
    @toggle-modal="isCreateModalOpened = false"
  />
  <edit-form
    v-if="isEditModalOpened"
    :assignment-metadata="selectedMetadata[0]"
    :is-edit-modal-opened="isEditModalOpened"
    :refresh-data="getAllAssignmentMetadata"
    @toggle-modal="isEditModalOpened = false"
  />
  <delete-modal
    v-if="isDeleteModalOpened"
    :is-deleting="isDeleteModalOpened"
    @cancel-delete="isDeleteModalOpened = false"
    @toggle-modal="isDeleteModalOpened = false"
    @confirm-delete="openConfirmationModal('delete')"
  />
  <confirmation-modal
    :is-confirmation-modal-opened="isConfirmationModalOpened"
    delete-text="delete"
    @confirm-action="deleteAssignmentMetadata"
    @close-confirmation-modal="isConfirmationModalOpened = false"
    @toggle-modal="isConfirmationModalOpened = false"
  />
  <main-section>
    <loader v-if="isLoading" />
    <div v-else>
      <table-action-header
        :icon="['fas', 'add']"
        :permission-id="permissions.CreateAssignment"
        :title="$t('AssignmentMetadata.Title')"
        description=" "
        button-text="Create"
        @execute-method="toggleCreateModal"
      />
      <custom-table
        :options="options"
        :table-data="assignmentMetadata"
        :table-headers="tableHeaders"
        @force-refresh="getAllAssignmentMetadata()"
        @open-data-modal="displayData"
        @selected-rows="getSelectedRow"
      />
    </div>
  </main-section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MainSection from "@/core/Components/MainSection.vue";
import Loader from "@/core/Components/Loader.vue";
import TableActionHeader from "@/core/Components/TableActionHeader.vue";
import CustomTable from "@/core/Components/CustomTable.vue";
import AssignmentMetadataService from "@/modules/Devices/Submodules/AssignmentMetadata/Services/AssignmentMetadataService";
import { AssignmentMetadata } from "@/modules/Devices/Submodules/AssignmentMetadata/Services/AssignmentMetadataTypes";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import LastDataPreview from "@/modules/Devices/Submodules/Assignments/Components/LastDataPreview.vue";
import CreateForm from "../Components/CreateForm.vue";
import DeleteModal from "@/core/Components/Notifications/DeleteModal.vue";
import ConfirmationModal from "@/core/Components/Notifications/ConfirmationModal.vue";
import EditForm from "../Components/EditForm.vue";
import { Permissions } from "@/core/Services/Enums";
import { TableDropdownItem } from "@/core/Services/GlobalTypes";

export default defineComponent({
  components: {
    ConfirmationModal,
    DeleteModal,
    LastDataPreview,
    CustomTable,
    TableActionHeader,
    Loader,
    MainSection,
    CreateForm,
    EditForm,
  },
  data() {
    return {
      assignmentMetadataService: new AssignmentMetadataService(),
      assignmentMetadata: [] as AssignmentMetadata[],
      toast: useToast(),
      selectedMetadata: [] as AssignmentMetadata[],
      dataForModal: {} as any,
      permissions: Permissions,
      isLoading: false,
      isDataSourceLoading: false,
      isDataPreviewModalOpened: false,
      isCreateModalOpened: false,
      isDeleteModalOpened: false,
      isConfirmationModalOpened: false,
      isEditModalOpened: false,
      tableHeaders: {
        id: { header: "ID", visible: false },
        name: { header: "Name", visible: true },
        "metadataType.name": { header: "Metadata Type", visible: true },
        data: { header: "Data", visible: true },
      },
      options: {
        edit: {
          header: "Action.Edit",
          icon: ["fas", "pen"],
          permissionId: Permissions.EditAssignment,
          action: this.toggleEditModal,
        },
        delete: {
          header: "Action.Delete",
          icon: ["fas", "trash"],
          permissionId: Permissions.DeleteAssignment,
          action: this.toggleDeleteModal,
        },
        /* removeRelation: {
          header: "Remove metadata relation",
          icon: ["fas", "link-slash"],
          permissionId: 300,
          action: this.toggleDeleteRelationModal,
        }, */
      } as TableDropdownItem,
    };
  },

  async created() {
    this.isLoading = true;
    try {
      await this.getAllAssignmentMetadata();
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async getAllAssignmentMetadata() {
      this.isDataSourceLoading = true;
      try {
        this.assignmentMetadata =
          await this.assignmentMetadataService.getAllAssignmentMetadata();
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isDataSourceLoading = false;
      }
    },
    async deleteAssignmentMetadata() {
      try {
        await this.assignmentMetadataService.deleteAssignmentMetadata(
          this.selectedMetadata[0].id
        );
        this.toast.success((this as any).$t("General.DeletedSuccessfully"));
        this.isDeleteModalOpened = false;
        this.isConfirmationModalOpened = false;
        await this.getAllAssignmentMetadata();
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      }
    },
    getSelectedRow(selectedRow: AssignmentMetadata[]) {
      this.selectedMetadata = selectedRow;
    },
    displayData(data: any) {
      this.isDataPreviewModalOpened = true;
      this.dataForModal = data;
    },
    openConfirmationModal(key: string) {
      this.isConfirmationModalOpened = true;
    },
    toggleCreateModal() {
      this.isCreateModalOpened = !this.isCreateModalOpened;
    },
    toggleDeleteModal() {
      this.isDeleteModalOpened = !this.isDeleteModalOpened;
    },
    toggleEditModal() {
      this.isEditModalOpened = !this.isEditModalOpened;
    },
  },
});
</script>

<style scoped></style>
